import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import {
  Link,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
  IconButton,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import parse from 'html-react-parser';
import _ from 'lodash';
import React, { useState, useEffect } from 'react';
import { MessageTypeConstants } from '../../../services/bot/Message';
import { useDispatch, useSelector } from 'react-redux';
import { setShowNotificationPopUp } from '../../../state/actions/chats';

const InfoIconBlock = styled(InfoIcon)(({ theme }) => ({
  verticalAlign: 'top',
  marginRight: 10,
  color: theme.palette.info.main,
}));

const ErrorIconBlock = styled(ErrorIcon)(({ theme }) => ({
  verticalAlign: 'top',
  marginRight: 10,
  color: theme.palette.error.main,
}));

const ListBlock = styled(List)(() => ({
  '&:hover': { backgroundColor: 'transparent' },
}));

const IconBtnBlock = styled(IconButton)(() => ({
  '&:hover': { backgroundColor: 'transparent' },
}));

const ListItemBlock = styled(ListItem)(({ isChecked }) => ({
  backgroundColor: isChecked ? 'rgb(230,246,250)' : '',
}));

const DialogTitleBlock = styled(DialogTitle)(() => ({ paddingBottom: 0 }));

const FMNotificationComponent = ({
  message,
  dismissNotifications,
  handleNewClose,
  messageArr = [],
  conversationId,
}) => {
  // safer handling
  const notifications = messageArr?.[conversationId] || [];
  const showNotificationPopUp = useSelector((state) => state.chats.showNotificationPopUp);

  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState([]);
  const dispatch = useDispatch();


  useEffect(() => {
    if (showNotificationPopUp && !_.isEmpty(message)) {
      setOpen(true);
    }
  }, [message]);

  const handleDialogClose = () => {
    setChecked([]);
    setOpen(false);
    handleNewClose();
    dispatch(setShowNotificationPopUp(false));
  };

  const dismissSelectedNotifications = () => {
    dismissNotifications(checked.map(ch => ch?.uuid || ch?.messageId));
    setChecked([]);
    setOpen(false);
    dispatch(setShowNotificationPopUp(false));
  };

  const isAllSelected =
    notifications.length > 0 && checked.length === notifications.length;

  const handleToggle = item => () => {
    const id = item?.uuid || item?.messageId;

    setChecked(prev => {
      const exists = prev.some(ch => (ch.uuid || ch.messageId) === id);
      return exists
        ? prev.filter(ch => (ch.uuid || ch.messageId) !== id)
        : [...prev, item];
    });
  };

  const handleSelectAll = e => {
    setChecked(e.target.checked ? notifications : []);
  };

  // reverse once instead of repeating array[array.length - 1 - index]
  const reversedList = [...notifications].reverse();

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      PaperProps={{ style: { borderRadius: 10, maxHeight: 400 } }}
    >
      <DialogTitleBlock>
        <span>Notifications</span>

        <IconButton
          disableRipple
          edge="end"
          style={{ position: 'relative', left: '70%' }}
          onClick={handleDialogClose}
          size="large"
        >
          <CloseIcon />
        </IconButton>

        <Box style={{ marginLeft: '4px' }}>
          <Checkbox
            checked={isAllSelected}
            color="primary"
            onChange={handleSelectAll}
            indeterminate={
              checked.length > 0 && checked.length < notifications.length
            }
          />

          <Link
            component="button"
            variant="body2"
            underline="none"
            sx={
              !checked.length && {
                color: 'gray',
                cursor: 'default',
                pointerEvents: 'none',
              }
            }
            onClick={dismissSelectedNotifications}
          >
            {`Dismiss (${checked.length}) notifications selected`}
          </Link>
        </Box>
      </DialogTitleBlock>

      <DialogContent PaperProps={{ style: { maxHeight: 200, overflow: 'auto', paddingTop: 0 } }}>
        <DialogContentText id="alert-dialog-description">
          <ListBlock>
            {reversedList?.map(item => {
              if (typeof item?.message !== 'string') return null;

              const id = item.uuid || item.messageId;
              const isChecked =
                checked.some(ch => (ch.uuid || ch.messageId) === id);

              return (
                <ListItemBlock
                  key={id}
                  style={{ cursor: 'default' }}
                  sx={!item?.markAsOld && { backgroundColor: 'rgb(254,247,225)' }}
                  isChecked={isChecked}
                  disableRipple
                  dense
                  button
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      color="primary"
                      onChange={handleToggle(item)}
                      checked={isChecked}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>

                  {/* Notification Type Sections - unchanged */}
                  {item.messageType ===
                    MessageTypeConstants.MESSAGE_TYPE_STD_NOTIFICATION && (
                      <>
                        <ListItemText
                          primary={<div style={{ fontWeight: 'bold' }}>Standard Notification</div>}
                          secondary={<div>{parse(item.message)}</div>}
                        />
                        <IconBtnBlock disableRipple style={{ cursor: 'default' }} edge="end" size="large">
                          <InfoIconBlock />
                        </IconBtnBlock>
                      </>
                    )}

                  {item.messageType ===
                    MessageTypeConstants.MESSAGE_TYPE_CRITICAL_NOTIFICATION && (
                      <>
                        <ListItemText
                          primary={<div style={{ fontWeight: 'bold' }}>Critical Notification</div>}
                          secondary={<div>{parse(item.message)}</div>}
                        />
                        <IconBtnBlock disableRipple style={{ cursor: 'default' }} edge="end" size="large">
                          <ErrorIconBlock />
                        </IconBtnBlock>
                      </>
                    )}

                  {item.messageType ===
                    MessageTypeConstants.MESSAGE_TYPE_AUTHORIZATION_REQUEST && (
                      <ListItemText
                        primary={<div style={{ fontWeight: 'bold' }}>Authorization Request</div>}
                        secondary={<div>{parse(item.message)}</div>}
                      />
                    )}
                </ListItemBlock>
              );
            })}
          </ListBlock>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default FMNotificationComponent;
